import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { MediaItem, MediaItemData } from '@/core/MediaItem/MediaItem.model';
import { VideoPlayer } from '@/components/ui/VideoPlayer';
import { EditorMediaCard } from './EditorMediaCard';
import { EditorLibraryLayout } from './EditorLibraryLayout';
import {
  ScrollableContainer,
  MediaGrid,
  MediaGridMobile,
} from '@/components/MediaContainer';
import { editorService } from '../services/editorService';
import { VideoReviewModal } from '@/domains/LibraryView/components/VideoReviewModal/VideoReviewModal';
import { UserRole } from '@/services/urlService';
import { ChevronDownIcon } from "@/components/icons";

interface EditorContext {
  clientId: string;
  clientName?: string;
  editorToken?: string;
}

export const EditorVideoLibrary: React.FC = () => {
  const navigate = useNavigate();
  const { clientId, editorToken } = useOutletContext<EditorContext>();
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState<MediaItem | null>(null);
  const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
  const [isReviewModeOpen, setIsReviewModeOpen] = useState(false);
  const LIMIT = 50;

  useEffect(() => {
    if (!editorToken || editorToken.length === 0) {
      console.error('⚠️ No editor token available in context');
      setError('Authentication token not found. Please log in again.');
      setIsLoading(false);
      return;
    }

    loadMedia(true, editorToken);
  }, [clientId, editorToken]);

  const loadMedia = async (reset = false, token?: string) => {
    const tokenToUse = token || editorToken;

    if (!tokenToUse || tokenToUse.length === 0) {
      console.error('⚠️ No authentication token available for media fetch');
      setError('No authentication token available');
      setIsLoading(false);
      setIsLoadingMore(false);
      return;
    }

    if (reset) {
      setIsLoading(true);
      setOffset(0);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const currentOffset = reset ? 0 : offset;
      const data = await editorService.getMediaItems(
        clientId,
        'videos',
        LIMIT,
        currentOffset
      );

      if (!data.success || !data.mediaItems) {
        throw new Error(data.error || 'Failed to load videos');
      }

      const items = MediaItem.fromBackendArray(
        data.mediaItems,
        UserRole.EDITOR,
        clientId,
        tokenToUse
      );

      if (reset) {
        setMediaItems(items);
      } else {
        setMediaItems(prev => [...prev, ...items]);
      }

      setHasMore(items.length === LIMIT);
      setOffset(currentOffset + items.length);
    } catch (err) {
      console.error('Load media error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load videos');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!isLoadingMore && hasMore) {
      loadMedia(false);
    }
  };

  const handleMediaClick = (item: MediaItem) => {
    if (item.isVideo()) {
      setSelectedVideo(item);
      setIsVideoPlayerOpen(true);
    }
  };

  const getStreamUrlByFileId = (fileId: string): string | undefined => {
    const mediaItem = mediaItems.find(
      item => item.fileIdentity === fileId
    );
    return mediaItem?.streamUrl;
  };

  const handleEnterReviewMode = () => {
    setIsReviewModeOpen(true);
  };

  const handleReviewModeClose = () => {
    setIsReviewModeOpen(false);
  };

  return (
    <EditorLibraryLayout
      activeMediaType="videos"
      onEnterReviewMode={handleEnterReviewMode}
      hasItems={mediaItems.length > 0}
    >
      {isLoading ? (
        <div className="p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 animate-spin text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Loading videos...
          </h3>
          <p className="text-gray-600">
            Please wait while we fetch your videos
          </p>
        </div>
      ) : error ? (
        <div className="p-12 text-center">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-red-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-red-600 font-medium mb-2">Failed to load videos</p>
          <p className="text-sm text-red-500 mb-4">{error}</p>
          <button
            onClick={() => loadMedia(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : mediaItems.length === 0 ? (
        <div className="p-12 text-center">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <p className="text-lg font-medium">No videos found</p>
        </div>
      ) : (
        <ScrollableContainer height="100%">
          {/* Desktop/Tablet: Multi-column grid with gaps (matches admin styling) */}
          <div className="hidden sm:block">
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(200px, calc((100% - 2rem) / 3), 320px), 1fr))' }}>
              {mediaItems.map((item) => (
                <EditorMediaCard
                  key={item.id}
                  item={item}
                  onMediaClick={handleMediaClick}
                />
              ))}
            </div>
          </div>

          {/* Mobile: 3-column edge-to-edge grid (matches admin styling) */}
          <div className="block sm:hidden">
            <MediaGridMobile>
              {mediaItems.map((item) => (
                <EditorMediaCard
                  key={item.id}
                  item={item}
                  onMediaClick={handleMediaClick}
                />
              ))}
            </MediaGridMobile>
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center py-3">
              <button
                onClick={loadMore}
                disabled={isLoadingMore}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200 disabled:text-gray-400"
              >
                {isLoadingMore ? (
                  <span className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-1.5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Loading more...
                  </span>
                ) : (
                  <span className="flex items-center">
                    Load more
                    <ChevronDownIcon className="w-4 h-4 ml-1" />
                  </span>
                )}
              </button>
            </div>
          )}
        </ScrollableContainer>
      )}

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayer
          mediaItem={selectedVideo}
          isOpen={isVideoPlayerOpen}
          onClose={() => {
            setIsVideoPlayerOpen(false);
            setSelectedVideo(null);
          }}
          getStreamUrlByFileId={getStreamUrlByFileId}
          clientId={clientId}
        />
      )}

      <VideoReviewModal
        isOpen={isReviewModeOpen}
        onClose={handleReviewModeClose}
        clientId={clientId}
        editorMode={true}
        editorToken={editorToken}
      />
    </EditorLibraryLayout>
  );
};
